import React, { useState, useEffect } from 'react';
import get from 'lodash/get';
import styled, { css } from 'styled-components';

// This MQ is outdated, entire PDP needs to be
// moved to new media queries 600/900/1200
import { MQ_FOR_MOBILE_BELOW } from 'web-ui-kit/media';

import { Body, BodyBold } from 'shared/fontStyles/StudioHeadings';

import DefaultVariant from './DefaultVariant';
import {
  CELL_PADDING,
  IMAGE_CELL_SIZE,
  SECTION_PADDING,
  UserFacingCopy,
  VariantSelectionState
} from './utils';
import ColorVariant from './ColorVariant';

export default function VariantAttributeSection ({
  attributeValues,
  availableProductsFromPrimary,
  isFirst,
  isLast,
  isPrimary,
  selectedValueForType,
  updateSelectedForAttribute,
  attributeType,
  variantProducts
}) {
  const [attributes, setAttributes] = useState(null);
  const [title, setTitle] = useState({ title: '', value: '' });

  const getAdditionalPropsForAttributeType = ({ product }) => {
    switch (attributeType) {
      case 'color':
        return { src: product && product.firstImageSrc() };
      default:
        return {};
    }
  };

  const getComponentType = () => {
    switch (attributeType) {
      case 'color':
        return ColorVariant;
      default:
        return DefaultVariant;
    }
  };

  const cellWidth = () => {
    switch (attributeType) {
      case 'color':
        return `minmax(${IMAGE_CELL_SIZE}px, ${IMAGE_CELL_SIZE}px)`;
      default:
        return `minmax(103px, 1fr)`;
    }
  };

  useEffect(() => {
    // Choose the component type based on the variant
    // Defaults to 'DefaultVariant' which is a text based button
    const Component = getComponentType();

    // Map the attribute values to components
    const attributeComponents = attributeValues && Object.keys(attributeValues).map(attributeValue => {
      // Gets the state and product for the attribute
      // in the return form of { state, product }
      const attributeInfo = getAttributeInfo({
        attributeValue,
        attributeValues,
        availableProductsFromPrimary,
        isPrimary,
        variantProducts,
        selectedValueForType
      });
      // Used to add custom props for variants that require additional props
      // Such as src for ColorVariant
      const additionalProps = getAdditionalPropsForAttributeType({ product: attributeInfo.product });

      return (
        <Component
          key={attributeValue}
          attributeType={attributeType}
          attributeValue={attributeValue}
          state={attributeInfo.state}
          updateSelectedForAttribute={updateSelectedForAttribute}
          {...additionalProps}
        />
      );
    });
    setAttributes(attributeComponents);
    setTitle({
      title: UserFacingCopy.SectionTitle[attributeType],
      value: `: ${selectedValueForType}`
    });
  }, [
    attributeType,
    attributeValues,
    availableProductsFromPrimary,
    selectedValueForType,
    updateSelectedForAttribute,
    variantProducts
  ]);

  return (
    <Container data-testid={'Container'} isFirst={isFirst} isLast={isLast}>
      <Title data-testid={'Title'}><BodyBold>{title.title}</BodyBold>{title.value}</Title>
      <Grid data-testid={'Grid'} cellWidth={cellWidth()}>
        {attributes}
      </Grid>
    </Container>
  );
}

// Gets the info relating to the active state of the
// cell which is linked by the attribute type and current
// selected primary attribute
export const getAttributeInfo = ({
  attributeValue,
  attributeValues,
  availableProductsFromPrimary,
  isPrimary,
  variantProducts,
  selectedValueForType
}) => {
  // List of all the products relating to this cell
  // For example a rug with 2 colors both of which
  // have a size of 9' x 9' will have two productIds
  // relating back to it
  const linkedProducts = attributeValues[attributeValue];

  // When a primary value is available (such as color for rugs)
  // we use that to determine which product we should be referring to
  // in the list of products from above
  const primaryLinkedProduct = availableProductsFromPrimary && linkedProducts
    ? linkedProducts.find(linkedProductId => availableProductsFromPrimary.includes(linkedProductId))
    : -1;

  // Primary attribute cells are always selectable
  // if there is not primary attribute we also do not want to restrict the selection of cells
  // otherwise the cell is only available if the productId is available in the selected
  // primary cell
  const available = isPrimary || !availableProductsFromPrimary || primaryLinkedProduct;
  const product = isPrimary
    ? variantProducts.find(variantProduct => variantProduct.id() === get(linkedProducts, '[0]', {}))
    : variantProducts.find(variantProduct => variantProduct.id() === primaryLinkedProduct);

  // default data
  const info = { state: VariantSelectionState.DEFAULT, product };
  const isSelected = attributeValue === selectedValueForType;
  // Update the state info based on the product data
  switch (true) {
    case !available:
      info.state = VariantSelectionState.DISABLED;
      break;
    case product && product.isOutOfStock():
      info.state = isSelected
        ? VariantSelectionState.OUT_OF_STOCK_SELECTED
        : VariantSelectionState.OUT_OF_STOCK;
      break;
    case isSelected:
      info.state = VariantSelectionState.SELECTED;
      break;
    default:
      info.state = VariantSelectionState.DEFAULT;
      break;
  }
  return info;
};

const Title = styled(Body)`
  padding-bottom: ${CELL_PADDING}px;
`;
Title.displayName = 'Title';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, ${props => props.cellWidth});
  grid-column-gap: ${CELL_PADDING}px;
  grid-row-gap: ${CELL_PADDING}px;
`;
Grid.displayName = 'Grid';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${SECTION_PADDING}px 0 ${props => props.isLast ? SECTION_PADDING : 0}px;

  ${props => props.isFirst && css`
    // The expected spacing between the next section and color
    // is 16px, the section above has an 8px bottom margin so
    // adding only 8px to create a 16px gap
    padding-top: 8px;
    // This MQ is outdated, entire PDP needs to be
    // moved to new media queries 600/900/1200
    ${MQ_FOR_MOBILE_BELOW} {
      padding-top: 0;
    }
  `}
`;
Container.displayName = 'Container';
